import { EarningType } from "../../../__generated__/graphql";
import { GetSalaryDetailsQuery } from "../../../__generated__/operations/graphql";
import React, { useMemo } from "react";
import { useCurrencyFormatter, useI18n } from "../../../hooks/i18n-hooks";
import VStack from "../../../ui_atoms/VStack";
import { StyleSheet } from "react-native";
import LabelValueText from "../../LabelValueText";
import { getIsPay13thSalaryWithSalary } from "../../../utils";
import { useTheme } from "../../../hooks/use-theme";

export const useStyles = () => {
    return useMemo(
        () =>
            StyleSheet.create({
                salaryDetailsItem: {
                    alignItems: "flex-start",
                    width: "100%",
                },
            }),
        [],
    );
};

type SalaryBreakdownProps = {
    salaryDetails: GetSalaryDetailsQuery;
    earningType: EarningType;
};

const SalaryBreakdown: React.FC<SalaryBreakdownProps> = ({ salaryDetails, earningType }) => {
    const styles = useStyles();
    const { fonts } = useTheme();
    const currencyFormatter = useCurrencyFormatter();
    const i18n = useI18n();
    const prefix = React.useMemo(() => {
        if (earningType === EarningType.Monthly) {
            return "month";
        } else if (earningType === EarningType.Hourly) {
            return "hour";
        } else if (earningType === EarningType.Daily) {
            return "day";
        } else if (earningType === EarningType.Package) {
            return "package";
        } else {
            throw new Error("Unknown earning type");
        }
    }, [earningType]);
    const isMonthSalary = earningType === EarningType.Monthly;
    // A 13x contract holds its 13th back, so the monthly figure is the base salary.
    const is13x =
        isMonthSalary &&
        !getIsPay13thSalaryWithSalary({
            earningType,
            has13thSalary: salaryDetails.getSalaryDetails.has13thSalary,
            pay13thSalaryWithSalary: salaryDetails.getSalaryDetails.pay13thSalaryWithSalary,
        });
    return (
        <>
            <VStack style={styles.salaryDetailsItem} gap="1/2">
                <LabelValueText
                    label={i18n.t(`screens.user.create_mission_salary.invoice_${prefix}_label`)}
                    value={currencyFormatter({
                        amountInCents: salaryDetails.getSalaryDetails.invoiceSalary,
                    })}
                    subValues={[
                        { label: i18n.t("screens.user.create_mission_salary.withoutTaxes") },
                    ]}
                    valueStyle={fonts.bodyLarge}
                />
                <LabelValueText
                    label={i18n.t(
                        is13x
                            ? "screens.user.create_mission_salary.base_month_label"
                            : `screens.user.create_mission_salary.gross_${prefix}_label`,
                    )}
                    value={currencyFormatter({
                        amountInCents: is13x
                            ? salaryDetails.getSalaryDetails.baseSalary
                            : salaryDetails.getSalaryDetails.grossSalary,
                    })}
                    subValues={
                        is13x
                            ? [
                                  {
                                      label: i18n.t(
                                          "screens.user.create_mission_salary.13th_salary_paid_separately",
                                      ),
                                  },
                              ]
                            : isMonthSalary && salaryDetails.getSalaryDetails.mandatory13thMonth
                              ? [
                                    {
                                        label: i18n.t(
                                            "screens.user.create_mission_salary.13th_salary_included",
                                        ),
                                    },
                                ]
                              : []
                    }
                    valueStyle={fonts.bodyLarge}
                />
                <LabelValueText
                    label={i18n.t(`screens.user.create_mission_salary.net_${prefix}_label`)}
                    value={currencyFormatter({
                        amountInCents: salaryDetails.getSalaryDetails.netSalary,
                    })}
                    valueStyle={fonts.bodyLarge}
                />
            </VStack>
        </>
    );
};

export default SalaryBreakdown;
